import React from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import IconButton from '@mui/material/IconButton';
import { alpha, styled } from '@mui/material/styles';
import ListItemText from '@mui/material/ListItemText';

import { uploadClasses } from './classes';
import { fileDataFromDocument } from './utils';
import Iconify from '../../../components/Iconify';
import { DocumentThumbnail } from './DocumentThumbnail';
import type { MultiFilePreviewDocumentProps } from './types';

// ----------------------------------------------------------------------

export function MultiFilePreviewDocument({
  sx,
  onRemove,
  onItemClick,
  lastNode,
  thumbnail,
  slotProps,
  firstNode,
  files = [],
  className,
  ...other
}: MultiFilePreviewDocumentProps) {
  return (
    <ListRoot
      thumbnail={thumbnail}
      className={mergeClasses([uploadClasses.uploadMultiPreview, className])}
      sx={sx}
      {...other}
    >
      {firstNode && <ItemNode thumbnail={thumbnail}>{firstNode}</ItemNode>}

      {files.map((file) => {
        const { name } = fileDataFromDocument(file);

        if (thumbnail) {
          return (
            <ItemThumbnail key={file.uri}>
              <DocumentThumbnail
                tooltip
                imageView
                uri={file.uri}
                fileName={file.fileName}
                onRemove={() => onRemove?.(file)}
                sx={[
                  (theme) => ({
                    width: 80,
                    height: 80,
                    border: `solid 1px ${theme.palette.grey['500_16'] || alpha(theme.palette.grey[500], 0.16)}`,
                    ...(onItemClick && { cursor: 'pointer' }),
                  }),
                ]}
                slotProps={{ icon: { sx: { width: 36, height: 36 } } }}
                onClick={() => onItemClick?.(file)}
                {...slotProps?.thumbnail}
              />
            </ItemThumbnail>
          );
        }

        return (
          <ItemRow
            key={file.uri}
            onClick={() => onItemClick?.(file)}
            sx={{ ...(onItemClick && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }) }}
          >
            <DocumentThumbnail uri={file.uri} fileName={file.fileName} {...slotProps?.thumbnail} />

            <ListItemText
              primary={name}
              slotProps={{
                primary: {
                  noWrap: true,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                },
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />

            {onRemove && (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(file); }}>
                <Iconify width={16} icon="mingcute:close-line" />
              </IconButton>
            )}
          </ItemRow>
        );
      })}

      {lastNode && <ItemNode thumbnail={thumbnail}>{lastNode}</ItemNode>}
    </ListRoot>
  );
}

// ----------------------------------------------------------------------

const ListRoot = styled('ul', {
  shouldForwardProp: (prop: string) => !['thumbnail', 'sx'].includes(prop),
})<Pick<MultiFilePreviewDocumentProps, 'thumbnail'>>(({ thumbnail, theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  ...(thumbnail && { flexWrap: 'wrap', flexDirection: 'row' }),
}));

const ItemThumbnail = styled('li')(() => ({ display: 'inline-flex' }));

const ItemRow = styled('li', {
  shouldForwardProp: (prop: string) => prop !== 'sx',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1, 1, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.grey['500_16'] || alpha(theme.palette.grey[500], 0.16)}`,
}));

const ItemNode = styled('li', {
  shouldForwardProp: (prop: string) => !['thumbnail', 'sx'].includes(prop),
})<Pick<MultiFilePreviewDocumentProps, 'thumbnail'>>(({ thumbnail }) => ({
  ...(thumbnail && { width: 'auto', display: 'inline-flex' }),
}));
